import { Link } from 'react-router-dom';
import { useUpdateCartItem, useRemoveCartItem } from '../../features/cart/useCart.js';
import { cn, formatMoney, imageProps } from '../../lib/format.js';
import { Spinner } from '../ui/Spinner.jsx';

/**
 * One line in the bag. Shared by the slide-over and the cart page so quantity
 * behaviour, stock warnings, and removal are identical in both.
 *
 * The quantity control is capped at what is actually available, so a shopper
 * cannot build a bag the checkout will then reject.
 */

export const CartLine = ({ line, compact = false }) => {
  const update = useUpdateCartItem();
  const remove = useRemoveCartItem();

  const busy = update.isPending || remove.isPending;
  const atMax = line.quantity >= line.maxQuantity;
  const lowStock = line.stock > 0 && line.stock <= 3;

  return (
    <li className={cn('flex gap-3', compact ? 'py-4' : 'py-6')}>
      <Link
        to={`/product/${line.slug}`}
        className="shrink-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {line.image ? (
          <img
            {...imageProps({ url: line.image, alt: line.title }, { sizes: '96px' })}
            className={cn('border border-mist object-cover', compact ? 'size-20' : 'size-24')}
          />
        ) : (
          <div className={cn('bg-mist/50', compact ? 'size-20' : 'size-24')} aria-hidden="true" />
        )}
      </Link>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={`/product/${line.slug}`}
              className="text-sm leading-snug text-ink hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              {line.title}
            </Link>

            {line.options?.length > 0 && (
              <p className="mt-0.5 font-mono text-2xs tracking-[0.06em] text-muted uppercase">
                {line.options.map((option) => option.value).join(' · ')}
              </p>
            )}

            <p className="mt-1 font-mono text-2xs text-muted" data-numeric>
              {formatMoney(line.unitPrice)} each
            </p>
          </div>

          <p className="shrink-0 text-sm font-medium text-ink" data-numeric>
            {formatMoney(line.lineTotal)}
          </p>
        </div>

        {lowStock && (
          <p className="mt-1.5 text-2xs text-danger">
            Only {line.stock} left — check out soon to keep it.
          </p>
        )}

        <div className="mt-2.5 flex items-center gap-3">
          <div className="flex h-9 items-center rounded border border-mist">
            <button
              type="button"
              disabled={busy}
              onClick={() => update.mutate({ itemId: line.id, quantity: line.quantity - 1 })}
              aria-label={line.quantity === 1 ? `Remove ${line.title}` : `Reduce quantity of ${line.title}`}
              className="grid size-8 place-items-center text-ink disabled:opacity-35"
            >
              {line.quantity === 1 ? (
                <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
                  <path d="M6.5 2h3l.5 1H13v1.5H3V3h3zM4 5.5h8l-.6 7.6a1 1 0 0 1-1 .9H5.6a1 1 0 0 1-1-.9z" />
                </svg>
              ) : (
                '−'
              )}
            </button>

            <span className="w-7 text-center font-mono text-xs" data-numeric aria-live="polite">
              {busy ? <Spinner className="mx-auto size-3" /> : line.quantity}
            </span>

            <button
              type="button"
              disabled={busy || atMax}
              onClick={() => update.mutate({ itemId: line.id, quantity: line.quantity + 1 })}
              aria-label={`Increase quantity of ${line.title}`}
              title={atMax ? `Only ${line.maxQuantity} available` : undefined}
              className="grid size-8 place-items-center text-ink disabled:opacity-35"
            >
              +
            </button>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={() => remove.mutate({ itemId: line.id })}
            className="text-2xs text-muted underline decoration-mist underline-offset-4 transition-colors hover:text-ink hover:decoration-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Remove
          </button>
        </div>
      </div>
    </li>
  );
};

export default CartLine;
